'use client';

import { c as _c } from "react/compiler-runtime";
import { trackEvent } from '@svs/nova-core/analytics';
import { useDialog } from '@svs/nova-foundation/nextjs/dialog';
import { resetCoupon } from '@svs/nova-poolbetting/core/action/coupon';
import { useCouponId, useCouponStatus } from '@svs/nova-poolbetting/core/nextjs/coupon';
import { COUPON_SETTINGS_SHEET, QUICK_PICK_SHEET } from '@svs/nova-poolbetting/schema/input';
import { CouponToolbox, CouponToolboxButton, CouponToolboxContent } from '@svs/nova-poolbetting/ui/CouponToolbox';
import { TipsenLink } from '@svs/nova-poolbetting/ui/TipsenLink';
import Dustbin from '@svs/ui/react/icons/Dustbin';
import Pix from '@svs/ui/react/icons/Pix';
import Settings from '@svs/ui/react/icons/Settings';
import Spelguide from '@svs/ui/react/icons/Spelguide';
import { useMediaQueryAbove } from '@svs/ui/react/media-query';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@svs/ui/react/tooltip';
import { createElement, type ReactNode, Suspense, useCallback } from 'react';
import { AddCouponsContainer, OpenSheetLink } from '@/components/index.browser';
import { useAutoTooltip, usePathname } from '@/lib/index.browser';
const ADD_COUPON_TOOLTIP_KEY = 'addCoupon';
const handleTracking = (label: string, pathname: string): void => {
  trackEvent({
    category: 'Nya tipsen',
    action: label,
    optLabel: pathname
  });
};

/**
 * Coupon toolbox for the play page.
 *
 * @returns Coupon toolbox for the play page.
 */
export default function PlayCouponToolbox() {
  const $ = _c(73);
  const [couponId, couponIndex] = useCouponId();
  const {
    showDialog
  } = useDialog();
  const isLargeScreen = useMediaQueryAbove("md");
  const pathname = usePathname();
  const {
    isOpen: isAddCouponTooltipOpen,
    isAutoShown: isAddCouponNewsShown,
    handleOpenChange: handleAddCouponTooltipOpenChange,
    ref: addCouponTooltipRef
  } = useAutoTooltip(ADD_COUPON_TOOLTIP_KEY);
  let t0;
  if ($[0] !== couponId || $[1] !== couponIndex) {
    t0 = {
      couponId,
      couponIndex
    };
    $[0] = couponId;
    $[1] = couponIndex;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const couponStatus = useCouponStatus(t0);
  let t1;
  if ($[3] !== couponId || $[4] !== couponIndex) {
    t1 = () => {
      (async () => {
        await resetCoupon({
          couponId,
          couponIndex
        });
      })();
    };
    $[3] = couponId;
    $[4] = couponIndex;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const resetCouponAction = t1;
  let t2;
  if ($[6] !== resetCouponAction || $[7] !== showDialog) {
    t2 = event => {
      event.preventDefault();
      (async () => {
        await showDialog({
          title: "Dina tecken kommer att rensas",
          description: createElement("p", {
            className: "text-center"
          }, "Vill du forts\xE4tta \xE4nd\xE5?"),
          buttons: {
            primary: {
              label: "Rensa",
              action: resetCouponAction
            },
            secondary: {
              label: "Avbryt"
            }
          }
        });
      })();
    };
    $[6] = resetCouponAction;
    $[7] = showDialog;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleResetCoupon = t2;
  const t3 = isLargeScreen ? "md" : "sm";
  let t4;
  if ($[9] !== pathname) {
    t4 = {
      category: "Nya tipsen",
      action: "Fyll i med PIX!",
      optLabel: pathname
    };
    $[9] = pathname;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Pix />;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4) {
    t6 = <OpenSheetLink sheet={QUICK_PICK_SHEET} queriesToKeep="ALL" trackingObject={t4}>{t5}Fyll i med PIX!</OpenSheetLink>;
    $[12] = t4;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t6) {
    t7 = <Suspense><CouponToolboxButton size={t3} asChild={true}>{t6}</CouponToolboxButton></Suspense>;
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== pathname) {
    t8 = () => {
      handleTracking("Spelguide", pathname);
    };
    $[17] = pathname;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = isLargeScreen ? "md" : "sm";
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <TipsenLink scroll={false} aria-label="Spelguide" queriesToKeep="ALL" href={{
      query: {
        modal: "gameGuide"
      }
    }}><Spelguide /></TipsenLink>;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t8 || $[21] !== t9) {
    t11 = <TooltipTrigger asChild={true}><CouponToolboxButton onClick={t8} size={t9} asChild={true} containerClassName="lg:hidden" className="min-w-0 px-2 md:px-3 lg:min-w-18">{t10}</CouponToolboxButton></TooltipTrigger>;
    $[20] = t8;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <TooltipContent side="top">Spelguide</TooltipContent>;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t11) {
    t13 = <Tooltip>{t11}{t12}</Tooltip>;
    $[24] = t11;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== pathname) {
    t14 = () => {
      handleTracking("L\xE4gg till kuponger", pathname);
    };
    $[26] = pathname;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = isLargeScreen ? "md" : "sm";
  let t16;
  if ($[28] !== t14 || $[29] !== t15) {
    t16 = <AddCouponsContainer onClick={t14} size={t15} />;
    $[28] = t14;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== addCouponTooltipRef || $[32] !== t16) {
    t17 = <TooltipTrigger asChild={true}><div ref={addCouponTooltipRef}>{t16}</div></TooltipTrigger>;
    $[31] = addCouponTooltipRef;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  const t18 = isAddCouponNewsShown ? "Nyhet! Spela fler kuponger samtidigt" : "L\xE4gg till fler kuponger";
  let t19;
  if ($[34] !== t18) {
    t19 = <TooltipContent side="top">{t18}</TooltipContent>;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== handleAddCouponTooltipOpenChange || $[37] !== isAddCouponTooltipOpen || $[38] !== t17 || $[39] !== t19) {
    t20 = <Tooltip open={isAddCouponTooltipOpen} onOpenChange={handleAddCouponTooltipOpenChange}>{t17}{t19}</Tooltip>;
    $[36] = handleAddCouponTooltipOpenChange;
    $[37] = isAddCouponTooltipOpen;
    $[38] = t17;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  const t21 = isLargeScreen ? "md" : "sm";
  let t22;
  if ($[41] !== pathname) {
    t22 = {
      category: "Nya tipsen",
      action: "Anpassa",
      optLabel: pathname
    };
    $[41] = pathname;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Settings />;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== t22) {
    t24 = <OpenSheetLink sheet={COUPON_SETTINGS_SHEET} aria-label={"Kuponginst\xE4llningar"} queriesToKeep="ALL" trackingObject={t22}>{t23}</OpenSheetLink>;
    $[44] = t22;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== t21 || $[47] !== t24) {
    t25 = <TooltipTrigger asChild={true}><CouponToolboxButton size={t21} asChild={true} className="min-w-0 px-2 md:px-3 lg:min-w-18">{t24}</CouponToolboxButton></TooltipTrigger>;
    $[46] = t21;
    $[47] = t24;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <TooltipContent side="top">Inställningar</TooltipContent>;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== t25) {
    t27 = <Suspense><Tooltip>{t25}{t26}</Tooltip></Suspense>;
    $[50] = t25;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  const t28 = couponStatus === "INVALID_NOT_STARTED";
  let t29;
  if ($[52] !== pathname) {
    t29 = () => {
      handleTracking("Rensa", pathname);
    };
    $[52] = pathname;
    $[53] = t29;
  } else {
    t29 = $[53];
  }
  const t30 = isLargeScreen ? "md" : "sm";
  let t31;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Dustbin />;
    $[54] = t31;
  } else {
    t31 = $[54];
  }
  let t32;
  if ($[55] !== t28 || $[56] !== t29 || $[57] !== t30) {
    t32 = <TooltipTrigger asChild={true}><CouponToolboxButton className="am:ml-auto w-full min-w-0 px-2 md:px-3 lg:min-w-18" aria-label="Rensa kupong" disabled={t28} data-testid="clearCoupon" onClick={t29} type="submit" size={t30}>{t31}</CouponToolboxButton></TooltipTrigger>;
    $[55] = t28;
    $[56] = t29;
    $[57] = t30;
    $[58] = t32;
  } else {
    t32 = $[58];
  }
  let t33;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <TooltipContent side="top">Rensa kupong</TooltipContent>;
    $[59] = t33;
  } else {
    t33 = $[59];
  }
  let t34;
  if ($[60] !== t32) {
    t34 = <Tooltip>{t32}{t33}</Tooltip>;
    $[60] = t32;
    $[61] = t34;
  } else {
    t34 = $[61];
  }
  let t35;
  if ($[62] !== handleResetCoupon || $[63] !== t34) {
    t35 = <form onSubmit={handleResetCoupon}>{t34}</form>;
    $[62] = handleResetCoupon;
    $[63] = t34;
    $[64] = t35;
  } else {
    t35 = $[64];
  }
  let t36;
  if ($[65] !== t13 || $[66] !== t20 || $[67] !== t27 || $[68] !== t35) {
    t36 = <TooltipProvider><div className="flex gap-2">{t13}{t20}{t27}{t35}</div></TooltipProvider>;
    $[65] = t13;
    $[66] = t20;
    $[67] = t27;
    $[68] = t35;
    $[69] = t36;
  } else {
    t36 = $[69];
  }
  let t37;
  if ($[70] !== t36 || $[71] !== t7) {
    t37 = <CouponToolbox><CouponToolboxContent className="bg-surface justify-between pt-0">{t7}{t36}</CouponToolboxContent></CouponToolbox>;
    $[70] = t36;
    $[71] = t7;
    $[72] = t37;
  } else {
    t37 = $[72];
  }
  return t37;
}